import React, { useState, useMemo } from 'react';
import { RefreshCw, Plus, Calendar, Search, CalendarRange } from 'lucide-react';
import { useReservations, DEFAULT_WINDOW_DAYS } from '../hooks/useReservations';
import { API_BASE_URL } from '../constants/config';
import ReservationTable from '../components/reservations/ReservationTable';
import ReservationModal from '../components/reservations/ReservationModal';
import { PageShell, Button, cn } from '../components/ui';
import { controlClasses } from '../components/ui/Field';

const allRestaurants = [
  'Inova Foz - Vila do Conde',
  'Inova Regio - Vila do Conde',
  'Inova Trindade - Porto',
  'Inova Beach - Azurara',
];

const allStatuses = [
  { value: '', label: 'Todos' },
  { value: 'pending', label: 'Pendente' },
  { value: 'confirmed', label: 'Confirmado' },
  { value: 'completed', label: 'Concluído' },
  { value: 'canceled', label: 'Cancelado' },
];

const filterControl = cn(controlClasses, 'py-1.5 text-sm');

const Reservations = () => {
  const token = localStorage.getItem('toms_auth_token');
  const onLogout = () => {
    localStorage.removeItem('toms_auth_token');
    window.location.href = '/login';
  };

  const {
    loading,
    error,
    filters,
    setFilters,
    fetchReservations,
    handleReservationUpdate,
    handleReservationCreate,
    filteredReservations,
  } = useReservations(token, API_BASE_URL, onLogout);

  const [activeTab, setActiveTab] = useState(allRestaurants[0]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [modalMode, setModalMode] = useState('edit');

  // How far back the server is asked for. null = everything.
  const wantedWindow = filters.dateWindow === '30d' ? DEFAULT_WINDOW_DAYS : null;
  const [fetchedWindow, setFetchedWindow] = useState(undefined);

  // Fetch on mount, and again when the user widens the window with "Mostrar
  // todas" — the older reservations are no longer already in the browser,
  // because they are no longer downloaded up front.
  //
  // Guarded by comparing windows rather than a plain `hasFetched` flag:
  // `fetchReservations` is a dependency (it must be, it is used here) and
  // `onLogout` is defined inline above, so this effect re-runs on every render.
  // The comparison is what stops it becoming a request loop.
  React.useEffect(() => {
    if (!token || fetchedWindow === wantedWindow) return;
    setFetchedWindow(wantedWindow);
    fetchReservations(true, wantedWindow);
  }, [token, wantedWindow, fetchedWindow, fetchReservations]);

  // Filter reservations by active tab
  const tabFilteredReservations = useMemo(
    () => filteredReservations.filter((res) => res.branch === activeTab),
    [filteredReservations, activeTab]
  );

  // Total number of people across the currently visible reservations
  const totalGuests = useMemo(
    () =>
      tabFilteredReservations.reduce(
        (sum, res) => sum + (Number(res.total_guest) || 0),
        0
      ),
    [tabFilteredReservations]
  );

  const handleEdit = (reservation) => {
    setSelectedReservation(reservation);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedReservation(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleModalSuccess = (reservation, mode) => {
    if (mode === 'edit') {
      handleReservationUpdate(reservation);
    } else {
      handleReservationCreate(reservation);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const headerExtra = (
    <>
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label htmlFor="futureOnly" className="text-sm font-medium text-muted">
            Mostrar:
          </label>
          <select
            id="futureOnly"
            value={filters.futureOnly}
            onChange={(e) => handleFilterChange('futureOnly', e.target.value)}
            className={filterControl}
          >
            <option value="all">Todas as Reservas</option>
            <option value="future">Reservas Futuras</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label htmlFor="status" className="text-sm font-medium text-muted">
            Estado:
          </label>
          <select
            id="status"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className={filterControl}
          >
            {allStatuses.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label htmlFor="date" className="text-sm font-medium text-muted">
            Dia:
          </label>
          <input
            type="date"
            id="date"
            value={filters.date}
            onChange={(e) => handleFilterChange('date', e.target.value)}
            className={filterControl}
          />
        </div>

        <div className="relative flex items-center">
          <Search className="absolute left-3 h-4 w-4 text-muted-2" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            placeholder="Pesquisar nome ou telefone"
            className={cn(filterControl, 'pl-9 w-60')}
          />
        </div>

        <Button
          variant="outline"
          size="sm"
          icon={CalendarRange}
          onClick={() =>
            handleFilterChange('dateWindow', filters.dateWindow === '30d' ? 'all' : '30d')
          }
        >
          {filters.dateWindow === '30d' ? 'Mostrar todas' : 'Últimos 30 dias'}
        </Button>

        <div className="ml-auto text-sm text-muted">
          Total: <span className="font-semibold text-foreground">{tabFilteredReservations.length}</span> reservas
          {' · '}
          <span className="font-semibold text-foreground">{totalGuests}</span> pessoas
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-4 -mx-4 sm:-mx-6 px-4 sm:px-6 border-t border-border pt-1">
        <div className="flex gap-1 flex-wrap">
          {allRestaurants.map((restaurant) => (
            <button
              key={restaurant}
              onClick={() => setActiveTab(restaurant)}
              className={cn(
                'px-4 py-2.5 text-sm font-semibold rounded-t-lg transition-colors border-b-2',
                activeTab === restaurant
                  ? 'border-brand text-brand bg-brand-soft'
                  : 'border-transparent text-muted hover:text-foreground hover:bg-surface-2'
              )}
            >
              {restaurant}
            </button>
          ))}
        </div>
      </div>
    </>
  );

  return (
    <PageShell
      title="Gestão de Reservas"
      icon={Calendar}
      actions={
        <>
          <Button
            variant="secondary"
            icon={RefreshCw}
            loading={loading}
            // Must pass the current window: defaulting would quietly drop back
            // to 30 days while "Mostrar todas" is still selected.
            onClick={() => fetchReservations(true, wantedWindow)}
          >
            {loading ? 'A Carregar...' : 'Atualizar'}
          </Button>
          <Button icon={Plus} onClick={handleCreate}>
            Criar Reserva
          </Button>
        </>
      }
      headerExtra={headerExtra}
    >
      {error && (
        <div className="mb-6 rounded-xl bg-status-canceled-soft text-status-canceled px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <ReservationTable reservations={tabFilteredReservations} onEdit={handleEdit} loading={loading} />

      <ReservationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        reservation={selectedReservation}
        apiUrl={API_BASE_URL}
        token={token}
        onSuccess={handleModalSuccess}
        mode={modalMode}
      />
    </PageShell>
  );
};

export default Reservations;
